import { getAuth } from "firebase/auth";

export interface WebSocketMessage {
    type: string;
    task_id?: string;
    meal_plan_id?: number;
    meal_plan_name?: string;
    timestamp?: string;
    user_id?: number;
    message?: string;
    error?: string;
    retry_count?: number;
}

class MealPlanWebSocketClient {
    private ws: WebSocket | null = null;
    private reconnectInterval: number = 5000;
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 5;
    private messageHandler: ((message: WebSocketMessage) => void) | null = null;
    private connectionStateHandler: ((connected: boolean) => void) | null = null;
    private shouldReconnect: boolean = true;
    private currentUserId: string | null = null;

    async connect(): Promise<void> {
        try {
            // Get the current user's Firebase token
            const auth = getAuth();
            const user = auth.currentUser;
            
            if (!user) {
                throw new Error("No authenticated user");
            }

            // TODO: Remove - Debug user auth
            console.log("[WebSocket] Authenticated user:", user.uid, user.email);

            // Check if this is a different user than the current connection
            if (this.currentUserId && this.currentUserId !== user.uid) {
                console.log("[WebSocket] User changed, closing existing connection");
                this.disconnect();
            }

            this.currentUserId = user.uid;
            const token = await user.getIdToken();
            
            // TODO: Remove - Debug token
            console.log("[WebSocket] Got Firebase token, length:", token.length);
            
            // Build WebSocket URL
            const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
            const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000/v2/mealplan";
            
            // Extract host and port from the base URL
            const urlParts = baseUrl.match(/^(https?:\/\/)([^/]+)(\/.*)?$/);
            if (!urlParts) {
                throw new Error("Invalid API URL format");
            }
            
            const hostAndPort = urlParts[2];
            const basePath = urlParts[3] || "";
            
            const url = `${protocol}//${hostAndPort}${basePath}/api/v1/ws/mealplan-events?token=${encodeURIComponent(token)}`;
            
            // TODO: Remove - Debug connection URL
            console.log("[WebSocket] Connecting to:", url.replace(token, "***TOKEN***"));
            
            this.ws = new WebSocket(url);
            
            this.ws.onopen = () => {
                // TODO: Remove - Debug connection opened
                console.log("[WebSocket] Connection opened successfully");
                this.reconnectAttempts = 0;
                this.connectionStateHandler?.(true);
            };
            
            this.ws.onmessage = (event) => {
                try {
                    // Handle ping/pong messages
                    if (event.data === "pong") {
                        // TODO: Remove - Debug pong received
                        console.log("[WebSocket] Received pong");
                        return;
                    }
                    
                    const message = JSON.parse(event.data);
                    // TODO: Remove - Debug message received
                    console.log("[WebSocket] Message received:", message);
                    this.messageHandler?.(message);
                } catch (error) {
                    // TODO: Remove - Debug parse error
                    console.error("[WebSocket] Failed to parse message:", error, "Raw data:", event.data);
                }
            };
            
            this.ws.onclose = (event) => {
                // TODO: Remove - Debug connection closed
                console.log(`[WebSocket] Connection closed - Code: ${event.code}, Reason: ${event.reason || "No reason"}, Clean: ${event.wasClean}`);
                this.connectionStateHandler?.(false);
                
                if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    // TODO: Remove - Debug reconnection attempt
                    console.log(`[WebSocket] Reconnecting in ${this.reconnectInterval}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                    setTimeout(() => this.connect(), this.reconnectInterval);
                } else if (!this.shouldReconnect) {
                    // TODO: Remove - Debug intentional disconnect
                    console.log("[WebSocket] Not reconnecting - intentional disconnect");
                } else {
                    // TODO: Remove - Debug max reconnects reached
                    console.log("[WebSocket] Not reconnecting - max attempts reached");
                }
            };
            
            this.ws.onerror = (error) => {
                // TODO: Remove - Debug connection error
                console.error("[WebSocket] Connection error:", error);
            };
            
            // Set up heartbeat
            this.startHeartbeat();
            
        } catch (error) {
            // TODO: Remove - Debug connection failure
            console.error("[WebSocket] Failed to connect:", error);
            this.connectionStateHandler?.(false);
            throw error;
        }
    }
    
    private startHeartbeat(): void {
        // TODO: Remove - Debug heartbeat started
        console.log("[WebSocket] Starting heartbeat (30s interval)");
        
        // Send ping every 30 seconds to keep connection alive
        const heartbeatInterval = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                // TODO: Remove - Debug ping sent
                console.log("[WebSocket] Sending ping");
                this.ws.send("ping");
            } else {
                // TODO: Remove - Debug heartbeat stopped
                console.log("[WebSocket] Stopping heartbeat - connection not open");
                clearInterval(heartbeatInterval);
            }
        }, 30000);
    }
    
    disconnect(): void {
        this.shouldReconnect = false;
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.currentUserId = null;
    }
    
    onMessage(handler: (message: WebSocketMessage) => void): void {
        this.messageHandler = handler;
    }
    
    onConnectionStateChange(handler: (connected: boolean) => void): void {
        this.connectionStateHandler = handler;
    }
    
    isConnected(): boolean {
        return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    }
}

// Export a singleton instance
export const mealPlanWebSocket = new MealPlanWebSocketClient();

 